/**
 * Partes e movimentações de um processo. Leitura por ora — esses dados chegam do
 * DataJud pela materialização; a edição manual não é o fluxo principal. Mostra quem
 * está em cada polo e o andamento em ordem cronológica reversa.
 */
import { Gavel, ListTree } from 'lucide-react';
import { useEffect, useState } from 'react';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { formatarData } from '@/lib/datas';

type Parte = {
  owner_tipo: string;
  owner_id: string;
  polo: string;
  tipo_participacao: string | null;
  principal: boolean;
};
type Movimentacao = {
  id: string;
  data: string | null;
  tipo: string | null;
  descricao: string | null;
};

export function PainelProcessoRelacoes({ processoId }: { processoId: string }) {
  const [partes, setPartes] = useState<Parte[]>([]);
  const [movs, setMovs] = useState<Movimentacao[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    void Promise.all([
      api.get<Parte[]>(`/cadastro/processos/${processoId}/partes`),
      api.get<Movimentacao[]>(`/cadastro/processos/${processoId}/movimentacoes`),
    ]).then(([rp, rm]) => {
      if (!vivo) return;
      setCarregando(false);
      if (rp.ok) setPartes(rp.dados);
      if (rm.ok) setMovs(rm.dados);
    });
    return () => {
      vivo = false;
    };
  }, [processoId]);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gavel className="size-4 text-accent" />
            Partes
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {estadoDaLista(carregando, partes.length === 0, 'Nenhuma parte registrada.') ?? (
            partes.map((p, i) => (
              <div
                key={i}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2"
              >
                <Chip aceso>{p.polo}</Chip>
                <span className="text-[15px] text-text">{p.tipo_participacao || p.owner_tipo}</span>
                {p.principal && <Chip>principal</Chip>}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListTree className="size-4 text-accent" />
            Movimentações
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {estadoDaLista(carregando, movs.length === 0, 'Nenhuma movimentação.') ?? (
            movs.map((m) => (
              <div key={m.id} className="rounded-md border border-border px-3 py-2">
                <div className="flex items-center gap-2">
                  {m.data && <span className="tipo-dado text-text-tertiary">{formatarData(m.data)}</span>}
                  {m.tipo && <Chip>{m.tipo}</Chip>}
                </div>
                {m.descricao && <p className="mt-1 tipo-corpo-pequeno text-text">{m.descricao}</p>}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </>
  );
}
